import React, { useCallback, useEffect, useRef, useState } from "react";

import { useLocation } from "react-router-dom";
import phoneNumberService from "src/services/phoneNumberService";
import conversationService from "src/services/conversationService";
import useChatChannel from "src/realtime/useChatChannel";

import ChatWindow from "./component/ChatWindow/ChatWindow";
import ContactSidebar from "./component/ContactSidebar/ContactSidebar";
import ConversationList from "./component/ConversationList/ConversationList";
import "src/stage/stage.css";
import "src/stage/wa-stage.css";
import "./inbox.css";

function Conversations() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const linkedConversation = params.get('conversation');
  const linkedWorkspace = params.get('workspace');
  const linkedNumber = params.get('number');
  const linkValid = Boolean(linkedConversation && linkedNumber && linkedWorkspace && [linkedConversation, linkedNumber, linkedWorkspace].every(value => /^[a-zA-Z0-9-]+$/.test(value)));
  const workspaceMatches = linkedWorkspace === localStorage.getItem('current_workspace');
  const linkBlocked = Boolean(linkedConversation && (!linkValid || !workspaceMatches));
  const phoneNumberId = linkValid && workspaceMatches ? linkedNumber : localStorage.getItem("phoneNumberId");

  const [isLoading, setIsLoading] = useState(true);
  const [refreshError, setRefreshError] = useState('');
  const [phoneNumber, setPhoneNumber] = useState(null);
  const [conversationRecords, setConversationRecords] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [isContactSidebarOpen, setIsContactSidebarOpen] = useState(false);

  const [refreshVersion, setRefreshVersion] = useState(0);
  const sequence = useRef(0);
  const loadedLink = useRef(null);
  // AUTO-WA-39: Home links straight to a filter, e.g. /conversation?filter=unread.
  const [query, setQuery] = useState(() => ({ page: 1, search: (params.get('search') || '').slice(0, 80), filter: ['unread', 'mine', 'unassigned'].includes(params.get('filter')) ? params.get('filter') : 'all', tag_id: '' }));
  const [pagination, setPagination] = useState(null);
  const [listLoading, setListLoading] = useState(false);
  const changeQuery = useCallback((patch) => {
    sequence.current++;
    setQuery(current => ({ ...current, ...patch, page: patch.page ?? 1 }));
  }, []);
  // Mobile navigation:
  // conversations -> chat -> contact
  const [mobileScreen, setMobileScreen] = useState(linkValid ? "chat" : "conversations");

  const initializeConversationWorkspace = useCallback(
    async (initial = false) => {
      const version = ++sequence.current;
      if (linkBlocked) { setActiveConversation(null); setConversationRecords([]); setIsContactSidebarOpen(false); setIsLoading(false); setListLoading(false); return; }
      if (initial) setIsLoading(true);
      setListLoading(true);
      try {
        const [phoneResponse, response, linkedResponse] = await Promise.all([
          phoneNumberService.show(phoneNumberId),
          conversationService.index(phoneNumberId, query),
          linkValid ? conversationService.show(phoneNumberId, linkedConversation) : Promise.resolve(null),
        ]);
        if (version !== sequence.current) return;
        const records = response?.data?.data ?? [];
        setPhoneNumber(phoneResponse?.data?.data ?? null);
        setConversationRecords(records);
        setPagination(response?.data?.meta ?? null);
        const linkKey = `${linkedWorkspace}:${linkedConversation}:${phoneNumberId}`;
        const newLink = linkValid && loadedLink.current !== linkKey;
        loadedLink.current = linkKey;
        setActiveConversation(
          (current) =>
            (newLink ? linkedResponse?.data?.data : null) ||
            records.find((item) => item.id === current?.id) ||
            linkedResponse?.data?.data ||
            records[0] ||
            null,
        );
        setRefreshVersion((value) => value + 1);
        setRefreshError('');
      } catch (error) {
        if (version !== sequence.current) return;
        if (initial || [401, 403, 404].includes(error?.response?.status)) {
          setConversationRecords([]);
          setActiveConversation(null);
          setIsContactSidebarOpen(false);
        }
        setRefreshError(error?.response?.status === 401
          ? 'Your session has expired. Reload this page to sign in again.'
          : linkValid && [403, 404].includes(error?.response?.status) ? 'This linked conversation is unavailable or you no longer have access.'
          : 'Inbox updates are temporarily unavailable. Retrying automatically.');
      } finally {
        if (version === sequence.current) { setIsLoading(false); setListLoading(false); }
      }
    },
    [phoneNumberId, query, linkedConversation, linkedWorkspace, linkValid, linkBlocked],
  );

  const cancelPending = useCallback(() => {
    sequence.current++;
  }, []);
  useEffect(() => {
    initializeConversationWorkspace();
    return cancelPending;
  }, [initializeConversationWorkspace, cancelPending]);

  const markConversationRead = useCallback((conversationId) => {
    setConversationRecords((previous) =>
      previous.map((item) =>
        item.id === conversationId ? { ...item, unread_count: 0 } : item,
      ),
    );
    setActiveConversation((current) =>
      current && current.id === conversationId
        ? { ...current, unread_count: 0 }
        : current,
    );
  }, []);

  const { connected: realtimeConnected } = useChatChannel(linkBlocked ? null : phoneNumberId, {
    onInvalidate: initializeConversationWorkspace,
    onReconnect: initializeConversationWorkspace,
  });

  const handleConversationSelection = (conversation) => {
    setActiveConversation(conversation);
    setIsContactSidebarOpen(false);
    setMobileScreen("chat");
  };

  const handleContactSidebarOpen = () => {
    if (!activeConversation) {
      return;
    }

    setIsContactSidebarOpen(true);
    setMobileScreen("contact");
  };

  const handleContactSidebarClose = () => {
    setIsContactSidebarOpen(false);
    if (window.innerWidth < 768) {
      setMobileScreen("chat");
    }
  };

  const handleBackToConversations = () => {
    setIsContactSidebarOpen(false);
    setMobileScreen("conversations");
  };

  const handleConversationDeleted = async () => {
    setActiveConversation(null);
    setIsContactSidebarOpen(false);
    setMobileScreen("conversations");

    await initializeConversationWorkspace();
  };

  if (isLoading) {
    return (
      <div className="wa-inbox-loading" role="status">
        <span className="ws-skeleton" style={{ width: 44, height: 44, borderRadius: 14 }} aria-hidden="true" />
        <span><strong>Opening your chats</strong><span>Getting the latest messages…</span></span>
      </div>
    );
  }

  return (
    <div className="wa-inbox ws-overlay">
      {linkBlocked && <p role="alert" className="wa-inbox-banner is-warn">This chat link belongs to another workspace. Switch to that workspace and open the link again.</p>}
      {refreshError && <p role="status" className="wa-inbox-banner">{refreshError}</p>}
      <div className={`wa-inbox-grid is-${mobileScreen}${isContactSidebarOpen ? " has-panel" : ""}`}>
        <div className="wa-inbox-col wa-inbox-col--list">
          <ConversationList
            query={query}
            onQueryChange={changeQuery}
            pagination={pagination}
            loading={listLoading}
            phoneNumber={phoneNumber}
            realtimeConnected={realtimeConnected}
            conversationRecords={conversationRecords}
            activeConversation={activeConversation}
            onConversationSelect={handleConversationSelection}
            onConversationDeleted={handleConversationDeleted}
          />
        </div>
        <div className="wa-inbox-col wa-inbox-col--chat">
          <ChatWindow
            phoneNumberId={phoneNumberId}
            activeConversation={activeConversation}
            onProfileClick={handleContactSidebarOpen}
            onBack={handleBackToConversations}
            onConversationDeleted={handleConversationDeleted}
            onConversationRead={markConversationRead}
            refreshVersion={refreshVersion}
            onAccessLost={initializeConversationWorkspace}
            realtimeConnected={realtimeConnected}
          />
        </div>
        <ContactSidebar
          conversation={activeConversation}
          isOpen={isContactSidebarOpen}
          onClose={handleContactSidebarClose}
        />
      </div>
    </div>
  );
}

export default Conversations;
